<template>
  <v-container class="external-changes-history">
    <v-row justify="center">
      <ViewHeader :title="$t('message.externalChanges.history.title')" />
      <v-col cols="12">
        <v-card class="external-changes-history__card">
          <v-container>
            <v-row>
              <v-col cols="12">
                <div class="d-flex align-end">
                  <div class="full-width">
                    <v-text-field
                      v-model="facilitiesSearchInputValue"
                      class="search-field"
                      :label="
                        $t('message.externalChanges.history.searchPlaceholder')
                      "
                      prepend-inner-icon="icon-search"
                      clearable
                      outlined
                      rounded
                      :hide-details="true"
                      @input="onFacilitySearch"
                    ></v-text-field>
                  </div>
                  <ui-button
                    color="primary"
                    @click="filterDialog = true"
                    class="ml-2"
                    icon="icon-filter"
                  >
                    {{ $t("message.common.filter") }}
                  </ui-button>
                </div>
              </v-col>
              <v-col cols="12" class="pb-0">
                <div class="d-flex flex-column">
                  <Pagination
                    :page="page"
                    :per-page="perPage"
                    :options="perPageOptions"
                    :total-count="totalCount"
                    :show-if-empty="true"
                    @change="handlePageChange"
                  >
                    <ExternalChangesSourceSelect
                      class="mr-5"
                      :loading="loading.list"
                      @update="handleSourceUpdate"
                    />
                  </Pagination>
                </div>
              </v-col>
              <v-col
                class="external-changes-history__list-wrapper"
                v-if="loaded && currentPageFacilities.length > 0"
              >
                <div class="external-changes-history__list">
                  <v-expansion-panels v-model="panel">
                    <ExternalChangeLocationItem
                      v-for="item in currentPageFacilities"
                      :key="item.id"
                      :item="item"
                      :source="changeSource"
                      :isHistoryMode="true"
                      @close="panel = null"
                    />
                  </v-expansion-panels>
                </div>

                <SectionLoader :status="loading.list" />
              </v-col>
              <v-col v-else-if="loaded && currentPageFacilities.length === 0">
                <div class="no-changes-history">
                  <img
                    src="@/static/externalChanges/noChanges.svg"
                    :alt="$t('message.externalChanges.noChangesAlt')"
                  />
                  <p class="no-changes-history__description">
                    {{ $t("message.externalChanges.noChangesHistory") }}
                  </p>
                </div>
              </v-col>
            </v-row>
            <SectionLoader :status="loading.view" />
          </v-container>
        </v-card>
      </v-col>

      <FilterDialog
        v-if="filterDialog"
        :show-open-status="true"
        :cities="cities"
        :facilities-ids="facilitiesIds"
        :facilities-groups="facilitiesGroupsIds"
        :chosen-search-type="searchType"
        :chosen-open-status="status.open"
        @setFacilitiesIds="facilitiesIds = $event"
        @setCities="cities = $event"
        @setFacilitiesGroupsIds="facilitiesGroupsIds = $event"
        @setSearchType="searchType = $event"
        @setOpenStatus="status.open = $event"
        @change="handleFiltersChange({ ...$event })"
        :handleClose="closeFilterDialog"
      />
    </v-row>
  </v-container>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { debounce } from "@/helpers/helpers";
import { EXTERNAL_CHANGES_STATUS } from "@/enums/external-changes";
import { gtmEngagement as gtmEngagementMixin } from "@/helpers/mixins/gtm-engagement";
import { gtmEvent__utmEmailNotification } from "@/helpers/gtm-events";
import { setCompanyIdFromQuery as setCompanyIdFromQueryMixin } from "@/helpers/mixins/set-company-id-from-query";
import { isUtmTaggedUrl } from "@/helpers/utm-tags";

import ViewHeader from "@/components/ui/ViewHeader.vue";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import Pagination from "@/components/List/Pagination";
import FilterDialog from "@/components/Filter/dialog";
import ExternalChangeLocationItem from "@/components/ExternalChanges/ExternalChangeLocationItem.vue";
import ExternalChangesSourceSelect from "@/components/ExternalChanges/ExternalChangesSourceSelect.vue";

export default {
  name: "ExternalChangesHistory",
  components: {
    ViewHeader,
    SectionLoader,
    Pagination,
    FilterDialog,
    ExternalChangeLocationItem,
    ExternalChangesSourceSelect
  },
  mixins: [gtmEngagementMixin, setCompanyIdFromQueryMixin],
  data() {
    return {
      page: 1,
      perPage: 10,
      perPageOptions: [10, 50, 100, 200, 500],
      panel: null,
      changeSource: null,
      loaded: false,
      loading: {
        view: false,
        list: false
      },
      facilitiesSearchInputValue: "",
      filterDialog: false,
      cities: [],
      facilitiesIds: [],
      facilitiesGroupsIds: [],
      searchType: null,
      status: {
        open: 0
      }
    };
  },

  computed: {
    ...mapState({
      currentPageFacilities: state => state.facilities.currentPageFacilities,
      totalCount: state => state.facilities.totalCount
    })
  },

  async created() {
    this.loading.view = true;
    await this.initFacilities({
      perPage: this.perPage,
      externalChangesStatuses: [
        EXTERNAL_CHANGES_STATUS.ACCEPTED,
        EXTERNAL_CHANGES_STATUS.REJECTED
      ]
    });
    this.loaded = true;
    this.loading.view = false;
  },

  mounted() {
    if (isUtmTaggedUrl(this.$route.query)) {
      gtmEvent__utmEmailNotification(this.$route.query);
    }

    this.mixin__setGtmEngagementTimeouts();
  },

  destroyed() {
    this.resetFacilitiesStore();
    this.resetFacilitiesGroupsStore();
  },

  methods: {
    ...mapActions({
      resetFacilitiesStore: "facilities/resetStore",
      resetFacilitiesGroupsStore: "facilitiesGroups/resetStore",
      initFacilities: "facilities/initFacilities"
    }),
    async fetchFacilities() {
      this.loading.list = true;
      this.panel = null;

      const params = {
        page: this.page,
        perPage: this.perPage,
        query: this.facilitiesSearchInputValue,
        isOwner: true,
        cities: this.cities,
        facilitiesIds: this.facilitiesIds,
        groups: this.facilitiesGroupsIds,
        externalChangesStatuses: [
          EXTERNAL_CHANGES_STATUS.ACCEPTED,
          EXTERNAL_CHANGES_STATUS.REJECTED
        ],
        externalChangesSources: [this.changeSource],
        openStatus: this.status.open
      };

      await this.$store.dispatch("facilities/fetchFacilities", params);
      this.loading.list = false;
    },

    async handlePageChange({ page, perPage }) {
      this.page = page;
      this.perPage = perPage;
      await this.fetchFacilities();
    },

    async handleFiltersChange({
      facilitiesIds = this.facilitiesIds,
      cities = this.cities,
      facilitiesGroupsIds = this.facilitiesGroupsIds,
      openStatus = this.status.open
    }) {
      this.page = 1;

      this.facilitiesIds = facilitiesIds;
      this.cities = cities;
      this.facilitiesGroupsIds = facilitiesGroupsIds;
      this.status.open = openStatus;

      await this.fetchFacilities();
    },

    closeFilterDialog() {
      this.filterDialog = false;
    },

    onFacilitySearch: debounce(async function() {
      this.page = 1;
      await this.fetchFacilities();
    }, 500),

    handleSourceUpdate(source) {
      this.changeSource = source;
      this.handleFiltersChange({});
    }
  }
};
</script>

<style lang="scss" scoped>
.external-changes-history {
  &__card {
    min-height: 650px;
  }

  &__select-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-left: $spacing;
  }

  &__list-wrapper {
    position: relative;
  }

  &__list {
    max-height: 628px;
    overflow-x: visible;
    overflow-y: auto;
  }
}

.v-expansion-panels {
  margin-bottom: 0;

  &:not(:last-child) {
    border-bottom: 1px solid $light-3;
  }
}

.no-changes-history {
  @include noData(650px);

  &__description {
    @include font-size(22px);
    text-align: center;
    margin: $spacing * 5 0 $spacing * 3;
  }
}
</style>
